import { match, P } from 'ts-pattern'
import * as styles from './project-dialog.css.ts'
import { type PickableRepository, RepositoryPickList } from './repository-pick-list.tsx'

export type RepositoryPickerSectionProps = {
  /** The heading the list carries when it has something to show. */
  label: string
  /** The heading used instead while a filter is narrowing the list. */
  filteredLabel: string
  /** What to say when the estate itself has nothing to offer — never while it is still arriving. */
  emptyMessage: string
  candidates: PickableRepository[]
  /** @see RepositoryPickListProps.total */
  total?: number
  pickedIds: ReadonlySet<string>
  onToggle: (repository: PickableRepository) => unknown
  unpickedDisabledReason?: string
  filter: string
  isFiltering: boolean
  isPending: boolean
  isError: boolean
}

/**
 * The pick list and the four things that can stand in for it, shared by the create and the add
 * dialogs. Separated because the states are the same in both and the ordering between them is the
 * part that is easy to get wrong: an empty list only means an empty estate once the list has arrived,
 * and claiming otherwise told readers their whole estate was already grouped on a slow load.
 */
export const RepositoryPickerSection: React.FC<RepositoryPickerSectionProps> = ({
  label,
  filteredLabel,
  emptyMessage,
  candidates,
  total,
  pickedIds,
  onToggle,
  unpickedDisabledReason,
  filter,
  isFiltering,
  isPending,
  isError,
}) =>
  match({ hasCandidates: candidates.length > 0, isPending, isError, isFiltering })
    .with({ hasCandidates: true }, () => (
      <RepositoryPickList
        label={isFiltering ? filteredLabel : label}
        candidates={candidates}
        total={total}
        pickedIds={pickedIds}
        onToggle={onToggle}
        unpickedDisabledReason={unpickedDisabledReason}
      />
    ))
    .with({ isPending: true }, () => <p className={styles.emptyNote}>Loading repositories…</p>)
    .with({ isError: true }, () => (
      <p className={styles.errorNote} role="alert">
        Failed to load repositories.
      </p>
    ))
    .with({ isFiltering: true }, () => (
      <p className={styles.emptyNote}>No repositories match &ldquo;{filter.trim()}&rdquo;.</p>
    ))
    .with(P._, () => <p className={styles.emptyNote}>{emptyMessage}</p>)
    .exhaustive()
